import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { formatDate, parseAndFormatDate } from "../../utils/dateFormatUtils";

interface EntranceExamDetailsTabProps {
  entranceDetails: any;
  isMobile: boolean;
}

const EntranceExamDetailsTab: React.FC<EntranceExamDetailsTabProps> = ({
  entranceDetails,
  isMobile,
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  const formatExpectedOnDate = (value: any): string => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";

    // Already in DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;

    // ISO / YYYY-MM-DD style
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const formatted = formatDate(raw);
      return formatted !== "-" ? formatted : raw;
    }

    // Fallback for other parseable date strings
    const parsed = parseAndFormatDate(raw);
    return parsed || raw;
  };

  const isScoreReceivedYes = (value: any): boolean => {
    const raw = String(value ?? "").trim().toLowerCase();
    return value === true || raw === "yes" || raw === "true";
  };

  const isScoreReceivedNo = (value: any): boolean => {
    const raw = String(value ?? "").trim().toLowerCase();
    return value === false || raw === "no" || raw === "false";
  };

  // Helper to map entrance exam details from the vertical step to the required format
  function mapEntranceExamDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return null;
    // Find and map core and language exam fields
    let coreExamApplied = null;
    let coreExamDetails: any[] = [];
    let languageExamApplied = null;
    let languageExamDetails: any[] = [];

    // Group core exam fields by suffix
    const coreGroups: Record<string, any> = {};
    const languageGroups: Record<string, any> = {};

    verticalStep?.fields.forEach((fieldObj: any) => {
      const key = Object.keys(fieldObj)?.[0];
      const value = fieldObj?.[key];
      // Suffix extraction: e.g. coreExamName_6 => _6
      const coreMatch = key.match(
        /^(coreExamName|coreScoreReceived|coreExamScore|coreTotalMarks|coreScoreExpectedOn)(_(\d+))?$/
      );
      const langMatch = key.match(
        /^(languageExamName|languageScoreReceived|languageExamScore|languageTotalMarks|languageScoreExpectedOn)(_(\d+))?$/
      );
      if (key === "coreExamApplied") {
        coreExamApplied = {
          key,
          label: "Have you applied for core exam?",
          value,
        };
      } else if (coreMatch) {
        const suffix = coreMatch[2] || "";
        if (!coreGroups[suffix]) coreGroups[suffix] = {};
        // Format label
        let label = "";
        if (coreMatch[1] === "coreExamName") label = "Core Exam Name";
        else if (coreMatch[1] === "coreScoreReceived")
          label = "Core Score Received";
        else if (coreMatch[1] === "coreExamScore") label = "Core Exam Score";
        else if (coreMatch[1] === "coreTotalMarks")
          label = "Core Exam Total Marks";
        else if (coreMatch[1] === "coreScoreExpectedOn")
          label = "Score Expected On";
        coreGroups[suffix][coreMatch[1]] = { key, label, value };
      } else if (key === "languageExamApplied") {
        languageExamApplied = {
          key,
          label: "Have you appeared for language exam?",
          value,
        };
      } else if (langMatch) {
        const suffix = langMatch[2] || "";
        if (!languageGroups[suffix]) languageGroups[suffix] = {};
        let label = "";
        if (langMatch[1] === "languageExamName") label = "Language Exam Name";
        else if (langMatch[1] === "languageScoreReceived")
          label = "Language Score Received";
        else if (langMatch[1] === "languageExamScore")
          label = "Language Exam Score";
        else if (langMatch[1] === "languageTotalMarks")
          label = "Language Exam Total Marks";
        else if (langMatch[1] === "languageScoreExpectedOn")
          label = "Score Expected On";
        languageGroups[suffix][langMatch[1]] = { key, label, value };
      }
    });

    // Flatten grouped core exam details into rows
    coreExamDetails = Object.keys(coreGroups).map((suffix) => {
      const group = coreGroups[suffix];
      return {
        examName: group.coreExamName?.value || "",
        scoreReceived: group.coreScoreReceived?.value || "",
        examScore: group.coreExamScore?.value || "",
        totalMarks: group.coreTotalMarks?.value || "",
        scoreExpectedOn: group.coreScoreExpectedOn?.value || "",
        labels: {
          examName: group.coreExamName?.label || "Core Exam Name",
          scoreReceived:
            group.coreScoreReceived?.label || "Core Score Received",
          examScore: group.coreExamScore?.label || "Core Exam Score",
          totalMarks: group.coreTotalMarks?.label || "Core Exam Total Marks",
          scoreExpectedOn:
            group.coreScoreExpectedOn?.label || "Score Expected On",
        },
      };
    });
    // Flatten grouped language exam details into rows
    languageExamDetails = Object.keys(languageGroups).map((suffix) => {
      const group = languageGroups[suffix];
      return {
        examName: group.languageExamName?.value || "",
        scoreReceived: group.languageScoreReceived?.value || "",
        examScore: group.languageExamScore?.value || "",
        totalMarks: group.languageTotalMarks?.value || "",
        scoreExpectedOn: group.languageScoreExpectedOn?.value || "",
        labels: {
          examName: group.languageExamName?.label || "Language Exam Name",
          scoreReceived:
            group.languageScoreReceived?.label || "Language Score Received",
          examScore: group.languageExamScore?.label || "Language Exam Score",
          totalMarks:
            group.languageTotalMarks?.label || "Language Exam Total Marks",
          scoreExpectedOn:
            group.languageScoreExpectedOn?.label || "Score Expected On",
        },
      };
    });

    return {
      coreExamApplied,
      coreExamDetails,
      languageExamApplied,
      languageExamDetails,
    };
  }

  // Normalize API shape when partnerStartedJourney is true
  // Incoming shape example:
  // coreExamDetails: [{ coreExam1: [{ label, key, value }, ...] }, { coreExam2: [...]}]
  // languageExamDetails: [{ languageExam1: [...] }, ...]
  function normalizeEntranceDetailsFromApiShape(raw: any) {
    if (!raw || typeof raw !== "object") return null;

    const normalizeGroupArray = (arr: any[]) => {
      if (!Array.isArray(arr)) return [];
      return arr.map((obj: any) => {
        const fieldsArr = Array.isArray(obj)
          ? obj
          : (Object.values(obj)?.[0] as any[]);
        if (!Array.isArray(fieldsArr))
          return {
            examName: "",
            scoreReceived: "",
            examScore: "",
            totalMarks: "",
            scoreExpectedOn: "",
            labels: {
              examName: "Exam Name",
              scoreReceived: "Score Received",
              examScore: "Exam Score",
              totalMarks: "Total Marks",
              scoreExpectedOn: "Score Expected On",
            },
          };
        const findByLabel = (contains: string) =>
          fieldsArr.find(
            (f: any) =>
              typeof f?.label === "string" &&
              f.label.toLowerCase().includes(contains)
          );

        const nameField = findByLabel("exam name");
        const scoreReceivedField = findByLabel("score received");
        const examScoreField = findByLabel("exam score");
        const totalMarksField = findByLabel("total marks");
        const scoreExpectedOnField = findByLabel("score expected on");

        return {
          examName: nameField?.value ?? "",
          scoreReceived: scoreReceivedField?.value ?? "",
          examScore: examScoreField?.value ?? "",
          totalMarks: totalMarksField?.value ?? "",
          scoreExpectedOn: scoreExpectedOnField?.value ?? "",
          labels: {
            examName: nameField?.label || "Exam Name",
            scoreReceived: scoreReceivedField?.label || "Score Received",
            examScore: examScoreField?.label || "Exam Score",
            totalMarks: totalMarksField?.label || "Total Marks",
            scoreExpectedOn:
              scoreExpectedOnField?.label || "Score Expected On",
          },
        };
      });
    };

    return {
      coreExamApplied: raw.coreExamApplied,
      coreExamDetails: normalizeGroupArray(raw.coreExamDetails),
      languageExamApplied: raw.languageExamApplied,
      languageExamDetails: normalizeGroupArray(raw.languageExamDetails),
    };
  }

  let entranceDetailsToShow = entranceDetails;
  // If partner journey data returns new API shape, normalize it
  const looksLikeApiShape =
    entranceDetailsToShow &&
    ((Array.isArray(entranceDetailsToShow?.coreExamDetails) &&
      entranceDetailsToShow.coreExamDetails.some((obj: any) =>
        Array.isArray((Object.values(obj) || [])[0]),
      )) ||
      (Array.isArray(entranceDetailsToShow?.languageExamDetails) &&
        entranceDetailsToShow.languageExamDetails.some((obj: any) =>
          Array.isArray((Object.values(obj) || [])[0]),
        )));
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    looksLikeApiShape
  ) {
    entranceDetailsToShow = normalizeEntranceDetailsFromApiShape(
      entranceDetailsToShow
    );
  }

  if (
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Entrance Exam Details' vertical step
    const entranceExamDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Entrance Exam Details"
    );
    if (entranceExamDetailsStep) {
      entranceDetailsToShow = mapEntranceExamDetailsFromVerticalStep(
        entranceExamDetailsStep
      );
    }
  }

  if (!entranceDetailsToShow) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const renderSectionHeading = (title: string, marginTop: number) => (
    <Grid item xs={12} sx={{ mt: marginTop, mb: 1 }}>
      <Box
        sx={{
          backgroundColor: "#edf6f6",
          borderLeft: "3px solid #00b3b5",
          borderRadius: "8px",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "15px", fontWeight: 600, color: "#6f6b7d" }}
        >
          {title}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <>
      {/* Core Exam Section */}
      {renderSectionHeading("Core Exam", 1)}
      <Grid item xs={12} sm={6} md={3}>
        <DetailRow
          item={{
            label:
              entranceDetailsToShow.coreExamApplied?.label ||
              "Have you applied for core exam?",
            value: (() => {
              const raw = entranceDetailsToShow.coreExamApplied?.value;
              const str = String(raw).toLowerCase();

              if (str === "yes" || str === "true") return "Yes";
              if (str === "no" || str === "false") return "No";

              return "No"; // default fallback
            })(),
            isVerified: false,
          }}
        />
      </Grid>
      {/* Render grouped core exam rows */}
      {entranceDetailsToShow.coreExamApplied?.value &&
        Array.isArray(entranceDetailsToShow.coreExamDetails) &&
        entranceDetailsToShow.coreExamDetails.map((row: any, idx: number) => (
          <Grid item xs={12} key={`core-row-${idx}`} sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3} md={3}>
                <DetailRow
                  item={{
                    label: row?.labels?.examName?.replace?.(/^Core /, "") ?? "",
                    value: row?.examName ?? "",
                    isVerified: false,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <DetailRow
                  item={{
                    label:
                      row?.labels?.scoreReceived?.replace?.(/^Core /, "") ?? "",
                    value:
                      typeof row?.scoreReceived === "boolean"
                        ? row?.scoreReceived
                          ? "Yes"
                          : "No"
                        : (row?.scoreReceived ?? ""),
                    isVerified: false,
                  }}
                />
              </Grid>
              {isScoreReceivedYes(row?.scoreReceived) && (
                <>
                  <Grid item xs={12} sm={3} md={3}>
                    <DetailRow
                      item={{
                        label:
                          row?.labels?.examScore?.replace?.(/^Core /, "") ?? "",
                        value: row?.examScore ?? "",
                        isVerified: false,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <DetailRow
                      item={{
                        label:
                          row?.labels?.totalMarks?.replace?.(/^Core /, "") ?? "",
                        value: row?.totalMarks ?? "",
                        isVerified: false,
                      }}
                    />
                  </Grid>
                </>
              )}
              {isScoreReceivedNo(row?.scoreReceived) && (
                <Grid item xs={12} sm={3} md={3}>
                  <DetailRow
                    item={{
                      label:
                        row?.labels?.scoreExpectedOn?.replace?.(/^Core /, "") ??
                        "Score Expected On",
                      value: formatExpectedOnDate(row?.scoreExpectedOn),
                      isVerified: false,
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        ))}

      {/* Language Exam Section */}
      {renderSectionHeading("Language Exam", 3)}
      <Grid item xs={12} sm={6} md={3}>
        <DetailRow
          item={{
            label:
              entranceDetailsToShow.languageExamApplied?.label ||
              "Have you appeared for language exam?",
            value: (() => {
              const raw = entranceDetailsToShow.languageExamApplied?.value;
              const str = String(raw).toLowerCase();

              if (str === "yes" || str === "true") return "Yes";
              if (str === "no" || str === "false") return "No";

              return "No"; // default fallback
            })(),
            isVerified: false,
          }}
        />
      </Grid>
      {/* Render grouped language exam rows */}
      {entranceDetailsToShow.languageExamApplied?.value &&
        Array.isArray(entranceDetailsToShow.languageExamDetails) &&
        entranceDetailsToShow.languageExamDetails.map(
          (row: any, idx: number) => (
            <Grid item xs={12} key={`language-row-${idx}`} sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3} md={3}>
                  <DetailRow
                    item={{
                      label:
                        row?.labels?.examName?.replace?.(/^Language /, "") ??
                        "",
                      value: row?.examName ?? "",
                      isVerified: false,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <DetailRow
                    item={{
                      label:
                        row?.labels?.scoreReceived?.replace?.(
                          /^Language /,
                          "",
                        ) ?? "",
                      value:
                        typeof row?.scoreReceived === "boolean"
                          ? row?.scoreReceived
                            ? "Yes"
                            : "No"
                          : (row?.scoreReceived ?? ""),
                      isVerified: false,
                    }}
                  />
                </Grid>
                {isScoreReceivedYes(row?.scoreReceived) && (
                  <>
                    <Grid item xs={12} sm={3} md={3}>
                      <DetailRow
                        item={{
                          label:
                            row?.labels?.examScore?.replace?.(/^Language /, "") ??
                            "",
                          value: row?.examScore ?? "",
                          isVerified: false,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={3}>
                      <DetailRow
                        item={{
                          label:
                            row?.labels?.totalMarks?.replace?.(/^Language /, "") ??
                            "",
                          value: row?.totalMarks ?? "",
                          isVerified: false,
                        }}
                      />
                    </Grid>
                  </>
                )}
                {isScoreReceivedNo(row?.scoreReceived) && (
                  <Grid item xs={12} sm={3} md={3}>
                    <DetailRow
                      item={{
                        label:
                          row?.labels?.scoreExpectedOn?.replace?.(
                            /^Language /,
                            "",
                          ) ?? "Score Expected On",
                        value: formatExpectedOnDate(row?.scoreExpectedOn),
                        isVerified: false,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          ),
        )}
    </>
  );
};

export default EntranceExamDetailsTab;
